import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import useEmblaCarousel from "embla-carousel-react";

import portfolioBarbara from "@/assets/portfolio-barbara.webp";
import portfolioOkubo from "@/assets/portfolio-okubo.webp";
import portfolioManuela from "@/assets/portfolio-manuela.webp";
import portfolioJuscilene from "@/assets/portfolio-juscilene.webp";
import portfolioAd4You from "@/assets/portfolio-ad4you.webp";

const projects = [
  {
    title: "Okubo Hype",
    category: "Estética Automotiva",
    image: portfolioOkubo,
  },
  {
    title: "Dra. Manuela Becker",
    category: "Advocacia e Consultoria Jurídica",
    image: portfolioManuela,
  },
  {
    title: "Dra. Juscilene Duarte",
    category: "Médica Veterinária",
    image: portfolioJuscilene,
  },
  {
    title: "Agência Ad4You",
    category: "Tráfego Pago",
    image: portfolioAd4You,
  },
  {
    title: "Dra. Bárbara Pinheiro",
    category: "Nutrição Metabólica",
    image: portfolioBarbara,
  },
];

const ProjectCard = ({
  project,
  index,
  onImageClick,
}: {
  project: (typeof projects)[0];
  index: number;
  onImageClick: (image: string) => void;
}) => (
  <motion.div
    className="group relative rounded-2xl overflow-hidden border border-white/[0.06] bg-card shadow-lg cursor-pointer"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.15 }}
    onClick={() => onImageClick(project.image)}
  >
    <div className="aspect-video overflow-hidden">
      <img
        src={project.image}
        alt={`Landing page ${project.title}`}
        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
    </div>
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-center justify-center">
      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/10 backdrop-blur-sm rounded-full p-4">
        <Search className="w-6 h-6 text-white" />
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-semibold text-sm">{project.title}</h3>
      <p className="text-xs text-muted-foreground">{project.category}</p>
    </div>
  </motion.div>
);

const PortfolioSection = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    loop: false,
  });

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <section id="portfolio" className="py-20 md:py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/[0.02] to-background" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm font-mono text-secondary uppercase tracking-widest">
            Projetos
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Exemplos de páginas criadas pela{" "}
            <span className="text-primary">Ad4You</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Alguns projetos criados pela Ad4You Pages.
          </p>
        </motion.div>

        {/* Responsive Carousel */}
        <div className="relative max-w-6xl mx-auto">
          {/* Swipe hint */}
          <p className="text-center text-xs text-muted-foreground mb-4 flex items-center justify-center gap-1.5 md:hidden">
            <ChevronLeft className="w-3 h-3" />
            Deslize para ver mais
            <ChevronRight className="w-3 h-3" />
          </p>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4 md:gap-6">
              {projects.map((project, i) => (
                <div
                  key={project.title}
                  className="flex-[0_0_85%] sm:flex-[0_0_60%] md:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)] min-w-0"
                >
                  <ProjectCard
                    project={project}
                    index={i}
                    onImageClick={setSelectedImage}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Desktop arrows */}
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 z-10 w-10 h-10 items-center justify-center rounded-full bg-card/80 backdrop-blur-sm border border-white/[0.08] hover:bg-card transition-colors"
            aria-label="Projeto anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 z-10 w-10 h-10 items-center justify-center rounded-full bg-card/80 backdrop-blur-sm border border-white/[0.08] hover:bg-card transition-colors"
            aria-label="Próximo projeto"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {projects.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? "w-6 h-2 bg-primary"
                    : "w-2 h-2 bg-muted-foreground/30"
                }`}
                aria-label={`Ir para projeto ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Credibility text */}
        <motion.p
          className="text-center text-sm text-muted-foreground mt-10 max-w-lg mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          Cada página é criada com foco em conversão, design profissional e experiência do usuário.
        </motion.p>

        {/* CTA */}
        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <a
            id="btn-cta-portfolio"
            href="https://wa.me/5519988278133?text=Olá,%20quero%20criar%20um%20site%20pela%20Ad4You%20Pages"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              import("@/lib/dataLayer").then(({ pushWhatsAppClick }) =>
                pushWhatsAppClick({
                  button_location: "portfolio_section",
                  plan_context: "geral",
                }),
              );
            }}
            className="group inline-flex items-center gap-2 rounded-xl bg-primary px-10 py-4 text-base font-semibold text-primary-foreground transition-all hover:opacity-90 glow-blue"
          >
            Criar meu site agora
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <p className="text-xs text-muted-foreground mt-3">
            Converse com nossa equipe pelo WhatsApp e comece seu projeto.
          </p>
        </motion.div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-2 bg-black/95 border-white/10">
          <AnimatePresence>
            {selectedImage && (
              <motion.img
                src={selectedImage}
                alt="Projeto ampliado"
                className="w-full h-auto rounded-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              />
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PortfolioSection;
